import { forwardRef, MouseEvent } from "react";
import { Link, LinkProps } from "react-router-dom";

type TrackedLinkProps = LinkProps & {
  /** Identifier of the page the click originates from, e.g. "deepseek-vs-stepfun". */
  source: string;
  /** Optional override for the GA4 event name. Defaults to "outbound_internal_click". */
  eventName?: string;
};

/**
 * Drop-in replacement for react-router-dom's <Link> that fires a GA4 event on click.
 *
 * Renders a real <a href> underneath, so crawlers see a normal internal link.
 * Analytics are fired client-side only, with no impact on SEO or rendered markup.
 */
export const TrackedLink = forwardRef<HTMLAnchorElement, TrackedLinkProps>(
  ({ source, eventName = "outbound_internal_click", to, onClick, children, ...rest }, ref) => {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      try {
        const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
        if (typeof gtag === "function") {
          const destination = typeof to === "string" ? to : "";
          const linkText = typeof children === "string" ? children : (e.currentTarget.textContent || "").trim();
          gtag("event", eventName, {
            event_category: "engagement",
            source_page: source,
            destination,
            link_text: linkText.slice(0, 100),
          });
        }
      } catch {
        // Never let analytics break navigation
      }
      onClick?.(e);
    };

    return (
      <Link ref={ref} to={to} onClick={handleClick} {...rest}>
        {children}
      </Link>
    );
  }
);

TrackedLink.displayName = "TrackedLink";
